import { HttpClient } from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CalendarEvent} from 'angular-calendar';
import {catchError, forkJoin, map, Observable, of, throwError} from 'rxjs';
import {InfoDialogComponent} from '@trivelope/triv-ui-tools-lib';
import {calendarColors, CalendarGroup, EventCategory, PortalEvent, PortalEventBackend} from '../classes/calendar';
import {Environment} from '../classes/environment';
import {CalAndNewsFileInfo, MyFile} from '../classes/fileInfo';
import {switchMap} from 'rxjs/operators';


@Injectable({providedIn: 'root'})
export class CalendarService {


	private calendarGroupCache: CalendarGroup[] = [];
	private subscribedEventIds: string[] = [];
	private unsubscribedEventIds: string[] = [];


	constructor(
		@Inject('environment') private environment: Environment,
		private matSnackBar: MatSnackBar,
		private httpClient: HttpClient,
		private matDialog: MatDialog,
	) {

    /*
    const fakeData = [
      {
        start: subDays(startOfDay(new Date()), 2),
        end: addDays(new Date(), 1),
        title: 'A 4 day event',
        color: { ...calendarColors['red'] },
        allDay: true,
        id: 123,
        meta: {}
      },
      {
        start: addDays( startOfDay(new Date()), 5 ),
        title: 'An event with no end date',
        color: { ...calendarColors['yellow'] },
        id: 124,
        meta: {}
      },
      {
        start: subDays(endOfMonth(new Date()), 3),
        end: addDays(endOfMonth(new Date()), 3),
        title: 'A long event that spans 2 months',
        color: { ...calendarColors['blue'] },
        allDay: true,
        id: 125,
        meta: {}
      },
      {
        start: addHours( addDays( startOfDay(new Date()), 7 ), 11 ),
        end: addHours( addDays( startOfDay(new Date()), 7 ), 13 ),
        title: "awesome event",
        color: { ...calendarColors['yellow'] },
        id: 127,
        meta: {}
      },
      {
        start: addHours( startOfDay(new Date()), 14 ),
        end: addHours( startOfDay(new Date()), 16 ),
        title: "awesome event n2",
        color: { ...calendarColors['red'] },
        id: 130,
        meta: {}
      },
      {
        start: addHours( startOfYesterday(), 10 ),
        end: addHours( startOfYesterday(), 15 ),
        title: "cool event",
        color: { ...calendarColors['yellow'] },
        id: 131,
        meta: {}
      },
      {
        start: addDays( addHours( startOfTomorrow(), 9 ), 11 ),
        end: addDays( addHours( startOfTomorrow(), 12 ), 11 ),
        title: "nice event",
        color: { ...calendarColors['blue'] },
        id: 132,
        meta: {}
      },
      {
        start: addHours( startOfTomorrow(), 13 ),
        end: addHours( startOfTomorrow(), 15 ),
        title: "top event",
        color: { ...calendarColors['red'] },
        id: 133,
        meta: {}
      },

    ];

    for( const d of fakeData ) {
      const pe = new PortalEvent();
      pe.tmsStart = d.start.getTime();
      pe.tmsEnd = d.end?.getTime() || pe.tmsStart + 1000;
      pe.name = d.title;
      pe.id = "" + d.id;
      pe.bReleased = !!Math.round( Math.random() );
      if( !this.eventCache.length ) {
        pe.place = "Hamburg";
        pe.description = `<h1>Hello</h1><p>This is rich text. <span style="color:#d93f0b;">Colors can be changed</span>.</p>
          <blockquote><h5><strong><span style="background-color:#d4c5f9;">Lorem Ipsum</span></strong>
          <span style="background-color:#d4c5f9;">&nbsp;is simply dummy text of the printing and typesetting industry. </span>
          <em><span style="background-color:#d4c5f9;">Lorem Ipsum has been the industry's standard dummy text ever since the 1500s</span></em>
          <span style="background-color:#d4c5f9;">, when an unknown p</span>rinter took a galley of type and scrambled it to make a type specimen book.</h5></blockquote>
        `;
      }
      this.eventCache.push( pe );
    }


    for( let i = 0; i < 30; i++ ) {
      const cg = new CalendarGroup();
      cg.vgId = "" + i;
      cg.vgName = "CalendarGroup " + i;
      this.calendarGroupCache.push( cg );
    }
    */
  }


  getPortalEvents(isInternal?: boolean): Observable<PortalEvent[]> {
    const queryParams = isInternal !== undefined ? `?nurIntern=${isInternal}` : '';

    return this.httpClient.get<{ dateien: CalAndNewsFileInfo[], veranstaltungen: PortalEventBackend[] }>(
      this.environment.backendUrl + 'event/all' + queryParams
    ).pipe(
      map(response => {
        if (!response || !response.veranstaltungen || !response.veranstaltungen.length) {
          return [];
        }

        const portalEvents: PortalEventBackend[] = [];
        for (const veranstaltung of response.veranstaltungen) {
          if (!portalEvents.find(p => p.veranstaltungId === veranstaltung.veranstaltungId)) {
            portalEvents.push(veranstaltung);
          }
        }

        const convertedEvents = portalEvents.map(e => this.convertPortalEventBtoF(e));

        // Gib die Events zurück, wenn der interne Kalender aktiviert ist, sonst filtere nach dem nurIntern-Flag
        if (isInternal === undefined) {
          return convertedEvents; // Gib alle Events zurück, wenn isInternal nicht gesetzt ist
        }
        return convertedEvents.filter(e => isInternal || !e.nurIntern);
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: { title: 'Fehler', message: 'Beim Abrufen der Veranstaltungen ist ein Fehler aufgetreten' }
        });
        return of([]);
      })
    );
  }

  getAllPortalEvents(): Observable<PortalEvent[]> {
    return this.httpClient.get<{ veranstaltungen: PortalEventBackend[] }>(
      this.environment.backendUrl + 'kalender/all'
    ).pipe(
      map(response => {

			if( !response || !response.veranstaltungen ) return [];

        return response.veranstaltungen.map(e => this.convertPortalEventBtoF(e));
      }),
      catchError(err => {
        // Zeige die genaue Fehlermeldung in der Konsole an
        console.error('Fehler beim Abrufen der Veranstaltungen vom Kalender-All Endpunkt:', err);

        // Zeige den Fehler im UI an
        this.matDialog.open(InfoDialogComponent, {
          data: { title: 'Fehler', message: 'Fehler beim Laden von internen Veranstaltungen.' }
        });
        return of([]);  // Leere Liste zurückgeben, damit die Anwendung nicht abstürzt
      })
    );
  }

  // endpoint for the Calendar view only
  getCalendarEvents(isInternal: boolean): Observable<CalendarEvent[]> {

	// Beide Arten von Events (intern und extern) laden, wenn der interne Kalender aktiv ist
    if (isInternal) {
      // Zuerst den normalen Endpunkt aufrufen
      return this.getPortalEvents().pipe(
        switchMap((portalEvents) => {
          // Danach den Kalender-All Endpunkt aufrufen
          return this.getAllPortalEvents().pipe(
            map((additionalEvents) => {
              // Beide Event-Listen zusammenführen
              const combinedEvents = [...portalEvents, ...additionalEvents];

              // Events filtern, um Duplikate anhand ihrer ID zu entfernen
              const uniqueEvents = combinedEvents.filter((event, index, self) =>
                index === self.findIndex((e) => e.id === event.id)
              );

              // Events formatieren
              return uniqueEvents
                .filter(e => e.tmsStart !== null) // Filtere nur nach vorhandenen Startzeitpunkten
                .map(e => this.convertPortalEventToCalendarEvent(e));
            })
          );
        })
      );
    } else {
      // Nur externe Veranstaltungen laden, wenn der interne Kalender nicht aktiv ist
      return this.getPortalEvents(false).pipe(
        map((portalEvents) => {
          return portalEvents
            .filter(e => e.tmsStart !== null && !e.nurIntern) // Nur externe Veranstaltungen
            .map(e => this.convertPortalEventToCalendarEvent(e)); // Konvertierung
        })
      );
    }
  }


  private convertPortalEventBtoF(peB: PortalEventBackend): PortalEvent {

    const pe = new PortalEvent();
    pe.bReleased = peB.veranstaltungFreigeben || false;
    pe.description = peB.veranstaltungsBeschreibung || '';
    pe.groups = peB.gruppen || [];
    pe.id = peB.veranstaltungId || '';
    pe.name = peB.veranstaltungName || '';
    pe.place = peB.veranstaltungOrt || '';

    pe.tmsStart = peB.veranstaltungAnfangsDatum ? new Date(peB.veranstaltungAnfangsDatum).getTime() : null;
    pe.tmsEnd = peB.veranstaltungEndDatum ? new Date(peB.veranstaltungEndDatum).getTime() : null;
    pe.tmsRegisterUntil = peB.veranstaltungAnmeldungsFrist ? new Date(peB.veranstaltungAnmeldungsFrist).getTime() : null;
    pe.tmsReminderMail = peB.veranstaltungErinnerungsmail ? new Date(peB.veranstaltungErinnerungsmail).getTime() : null;
    pe.bReminderMailSent = peB.veranstaltungErinnerungsmailGesendet ?? false;


    if (peB.dateien && peB.dateien.length) {
      pe.files = peB.dateien.map(d => {
        const mf = new MyFile();
        mf.id = d.dateiId;
        mf.file = new File([], d.dateiName);
        mf.percentageUploaded = 100;
        return mf;
      });
    }

    if( peB.assignedBenutzer && peB.assignedBenutzer.length ) {
		pe.assignedUsers = peB.assignedBenutzer;
    }

    pe.categoryId = peB.fragenKategorieId;
    pe.nurIntern = peB.nurIntern;

    return pe;

  }

  getEventFullInfo(id: string): Observable<PortalEvent | null> {

    return this.httpClient.get<{ veranstaltungen: PortalEventBackend[] }>(
      this.environment.backendUrl + 'event/' + id
    ).pipe(
      map(response => {
        if (!response || !response.veranstaltungen || !response.veranstaltungen.length) {
          return null;
        }
        return this.convertPortalEventBtoF(response.veranstaltungen[0]);
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Abrufen der Details des Ereignisses ist ein Fehler aufgetreten'}
        });
        return of(null);
      })
    );

  }

  saveEvent(portalEvent: PortalEvent): Observable<PortalEvent | null> {

    console.log('Veranstaltung wird gespeichert:', portalEvent); //ist hier schon leer


    return this.httpClient.post<{ verarbeitet: boolean, veranstaltungen: PortalEventBackend[] }>(
      this.environment.backendUrl + 'event/save',
      this.convertPortalEventFtoB(portalEvent)
    ).pipe(
      map(answer => {
        if (!answer || !answer.verarbeitet) {
          return null;
        }
        if (!answer.veranstaltungen || !answer.veranstaltungen.length) {
          return null;
        }
        return this.convertPortalEventBtoF(answer.veranstaltungen[0]);
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Speichern der Veranstaltung ist ein Fehler aufgetreten'}
        });
        return of(null);
      })
    );

  }

  releaseEvent(portalEventId: string): Observable<boolean> {


    console.log('POST URL:', this.environment.backendUrl + 'event/release/' + portalEventId);
    console.log('POST Body:', {}); // Hier werden die gesendeten Daten angezeigt


    return this.httpClient.post<{ verarbeitet: boolean, veranstaltungen: PortalEventBackend[] }>(
      this.environment.backendUrl + 'event/release/' + portalEventId,
      {}
    ).pipe(
      map(answer => {
        if (!answer || !answer.verarbeitet) {
          return false;
        }
        return true;
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Freigeben der Veranstaltung ist ein Fehler aufgetreten'}
        });
        return of(false);
      })
    );

  }


  private convertPortalEventFtoB(pe: PortalEvent): PortalEventBackend {

    const peB = new PortalEventBackend();
    peB.veranstaltungFreigeben = pe.bReleased;
    peB.veranstaltungsBeschreibung = pe.description;
    peB.gruppen = pe.groups;
    peB.veranstaltungId = pe.id;
    peB.veranstaltungName = pe.name;
    peB.veranstaltungOrt = pe.place;
    // Überprüfe und konvertiere die Zeitstempel in das richtige Format (Unix-Zeitstempel)
    peB.veranstaltungAnfangsDatum = pe.tmsStart ? new Date(pe.tmsStart).getTime() : null;
    peB.veranstaltungEndDatum = pe.tmsEnd ? new Date(pe.tmsEnd).getTime() : null;
    peB.veranstaltungAnmeldungsFrist = pe.tmsRegisterUntil ? new Date(pe.tmsRegisterUntil).getTime() : null;
    peB.veranstaltungErinnerungsmail = pe.tmsReminderMail ? new Date(pe.tmsReminderMail).getTime() : null;
    peB.veranstaltungErinnerungsmailGesendet = pe.bReminderMailSent;
    peB.fragenKategorieId = pe.categoryId;
    peB.nurIntern = pe.nurIntern;

    console.log(peB.gruppen ,'GRUPPEN');
    return peB;

  }

  deleteEvent(id: string): Observable<boolean> {

    return this.httpClient.delete<{ verarbeitet: boolean }>(
      this.environment.backendUrl + 'event/delete/' + id
    ).pipe(
      map(answer => {
        if (!answer || !answer.verarbeitet) {
          return false;
        }
        return true;
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Löschen der Veranstaltung ist ein Fehler aufgetreten'}
        });
        return of(false);
      })
    );

  }


  /*
  deleteCalendarGroup( id: string ): Observable<boolean> {

    return this.httpClient.post<boolean>(
      this.environment.backendUrl + 'calendar/delete',
      id
    ).pipe(
      map( answer => !!answer ),
      catchError( err => {
        this.matDialog.open( InfoDialogComponent, {
          data: { title: "Fehler", message: "Beim Löschen der Veranstaltungsgruppe ist ein Fehler aufgetreten" }
        });
        return of(false);
      })
    );

  }
  */

  private convertPortalEventToCalendarEvent(portalEvent: PortalEvent): CalendarEvent {

    let txtDesc = portalEvent.description
      .replace(/<[^>]+>/g, '');
    if (txtDesc.length > 70) {
      txtDesc = txtDesc.substring(0, 70) + '…';
    }

    return {
      start: portalEvent.tmsStart === null ? new Date(-30610227208000) : new Date(portalEvent.tmsStart),
      end: portalEvent.tmsEnd === null ? new Date(-30578691208000) : new Date(portalEvent.tmsEnd),
      title: portalEvent.name,
      color: {...calendarColors[portalEvent.bReleased ? 'blue' : 'grey']},
      id: portalEvent.id,
      meta: {
        bReleased: portalEvent.bReleased,
        description: txtDesc,
        subscribedUserIds: portalEvent.assignedUsers.filter( u => u.vbAngemeldet ).map( u => u.ebeId ),
        nurIntern: portalEvent.nurIntern // Hier wird das nurIntern-Flag hinzugefügt
      },
    };

  }


  getCalendarGroups(): Observable<CalendarGroup[]> {

    // return of( this.calendarGroupCache );
    return this.httpClient.get<CalendarGroup[]>(this.environment.backendUrl + 'eventGroups/all').pipe(
      map(cgroups => {
        if (!cgroups || !cgroups.length) {
          return [];
        }
        this.calendarGroupCache = cgroups;
        return cgroups;
      }),
      catchError(err => {
        this.matSnackBar.open('Beim Abrufen der Liste der Gruppen ist ein Fehler aufgetreten', 'Fehler');
        return [];
      })
    );

  }

  isUniqueCalendarName(calendarGroupId: string, name: string): boolean {

    const newName = name.toLocaleLowerCase();
    return !this.calendarGroupCache.find(cg => (cg.vgName.toLocaleLowerCase() === newName) && (cg.vgId != calendarGroupId));

  }

  saveCalendarGroup(calendarGroup: CalendarGroup): Observable<CalendarGroup | null> {

    if (!this.isUniqueCalendarName(calendarGroup.vgId, calendarGroup.vgName)) {
      this.matDialog.open(InfoDialogComponent, {
        data: {title: 'Fehler', message: 'Eine Gruppe mit diesem Namen existiert bereits.'}
      });
      return of(null);
    }


    return this.httpClient.post<{ verarbeitet: boolean, veranstaltungsGruppe: CalendarGroup }>(
      this.environment.backendUrl + 'eventGroup/save',
      calendarGroup
    ).pipe(
      map(response => {

        if (!response || !response.verarbeitet) {
          return null;
        }

        const savedCG = response.veranstaltungsGruppe;
        const oldCG = this.calendarGroupCache.find(cg => cg.vgId == savedCG.vgId);
        if (oldCG) {
          oldCG.vgName = savedCG.vgName;
          oldCG.vgAktiv = savedCG.vgAktiv;
		  oldCG.arbeitsgruppe = savedCG.arbeitsgruppe;
		  oldCG.vgChat = savedCG.vgChat;
        } else {
          this.calendarGroupCache.push(savedCG);
        }

        return savedCG;
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Speichern der Gruppe ist ein Fehler aufgetreten'}
        });
        return of(null);
      })
    );

  }

  subscribeUserToCalendarGroups(userId: string, calendarGroupIds: string[]): Observable<boolean> {

    return this.httpClient.post<{ verarbeitet: boolean }>(
      this.environment.backendUrl + 'eventGroup/assign/' + userId,
      calendarGroupIds
    ).pipe(
      map(answer => {
        if (!answer || !answer.verarbeitet) {
          return false;
        }
        return true;
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Zuweisen von Gruppen ist ein Fehler aufgetreten'}
        });
        return of(false);
      })
    );

  }

  getCalendarGroupsSubscribedToUser(userId: string): Observable<string[]> {

    return this.httpClient.get<CalendarGroup[]>(
      this.environment.backendUrl + 'eventGroups/all/assigned/' + userId,
    ).pipe(
      map(cGroups => {
        if (!cGroups) {
          return [];
        }
        if (!cGroups.length) {
          return [];
        }
        return cGroups.map(cg => cg.vgId);
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Abrufen der abonnierten Kalendergruppen ist ein Fehler aufgetreten.'}
        });
        return of([]);
      })
    );

  }

  getEventsInGroupsForWhichUserHasSubscribed(userId: string): Observable<PortalEvent[]> {

    return this.httpClient.get<{ veranstaltungen: PortalEventBackend[] }[]>(
      this.environment.backendUrl + 'eventGroups/all/assigned/' + userId,
    ).pipe(
      map(cGroups => {
        if (!cGroups) {
          return [];
        }
        if (!cGroups.length) {
          return [];
        }
        const events = [];
        for (const cGroup of cGroups) {
          events.push(...cGroup.veranstaltungen);
        }
        const uniqueEvents: PortalEventBackend[] = [];
        for (const event of events) {
          if (!uniqueEvents.find(e => e.veranstaltungId == event.veranstaltungId)) {
            uniqueEvents.push(event);
          }
        }
        return uniqueEvents.map(pb => this.convertPortalEventBtoF(pb));
      }),
      catchError(err => {
        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Abrufen von Ereignissen in abonnierten Kalendergruppen ist ein Fehler aufgetreten.'}
        });
        return of([]);
      })
    );

  }


	loadSubscribedEvents(userId: string): Observable<boolean> {

		this.subscribedEventIds = [];
		this.unsubscribedEventIds = [];

		return this.httpClient.get<PortalEventBackend []>(
			this.environment.backendUrl + 'veranstaltungKategorie/getAngemeldeteVeranstaltungen/' + userId
		).pipe(
			switchMap( answer => {

				if( !answer ) return of(false);

				for( const item of answer ) {
					this.subscribedEventIds.push(item.veranstaltungId);
				}
				return this.httpClient.get<PortalEventBackend []>(
					this.environment.backendUrl + 'veranstaltungKategorie/getAbgemeldeteVeranstaltungen/' + userId
				).pipe(
					map( response => {

						if( !response ) return false;

						for( const item of response ) {
							if( !item ) continue;
							this.unsubscribedEventIds.push(item.veranstaltungId);
						}
						return true; // Return true once everything is completed
					}),
					catchError(err => {
						console.error('Error getting Veranstaltungen:', err);
						return of(false);
					})
				);
			}),
			catchError(err => {
				this.matDialog.open(InfoDialogComponent, {
					data: {title: 'Fehler', message: 'Beim Abrufen von Veranstaltungen ist ein Fehler aufgetreten'}
				});
				return of(false);
			})
		);

	}


	getSubscribedEventIds(): string[] {
		return structuredClone(this.subscribedEventIds);
	}

  removeEventFromSubscribedEvents(eventId: string) {

    const nPos = this.subscribedEventIds.findIndex(id => id === eventId);
    if (nPos !== -1) {
      this.subscribedEventIds.splice(nPos, 1);
    }

    if (!this.unsubscribedEventIds.includes(eventId)) {
      this.unsubscribedEventIds.push(eventId);
    }

  }

  unsubscribeUsersFromEvent(eventId: string, userIds: string[]): Observable<boolean> {

    return this.httpClient.delete<{ verarbeitet: boolean }>(
      this.environment.backendUrl + 'event/unassign/' + eventId,
      {body: userIds}
    ).pipe(
      map(answer => {

        if (!answer || !answer.verarbeitet) {
          return false;
        }

        this.matSnackBar.open('Benutzer abgemeldet', 'Erfolg');

        return true;
      }),
      catchError(err => {

        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Speichern der Abonnements ist ein Fehler aufgetreten'}
        });
        return of(false);

      })
    );


  }

  subscribeToEvent(eventId: string): Observable<boolean> {

    return this.httpClient.post<{ verarbeitet: boolean }>(
      this.environment.backendUrl + 'event/assign/' + eventId,
      {}
    ).pipe(
      map(answer => {

        if (!answer || !answer.verarbeitet) {
          return false;
        }

        this.matSnackBar.open('Veranstaltung abonniert', 'Erfolg');

        if (!this.subscribedEventIds.includes(eventId)) {
          this.subscribedEventIds.push(eventId);
        }
        this.unsubscribedEventIds = this.unsubscribedEventIds.filter(id => id !== eventId);

        return true;

      }),
      catchError(err => {

        this.matDialog.open(InfoDialogComponent, {
          data: {title: 'Fehler', message: 'Beim Speichern der Abonnements ist ein Fehler aufgetreten'}
        });
        return of(false);

      })
    );

  }

	subscribeToEventWithAnswer( eventId: string ): Observable<boolean> {

		return this.httpClient.post<{ verarbeitet: boolean }>(
			this.environment.backendUrl + 'event/assign/' + eventId,
			{}
		).pipe(
			catchError( err => {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: 'Fehler', message: 'Beim Abonnieren der Veranstaltung ist ein Fehler aufgetreten' }
				});
				return of(null);
			}),
			switchMap( result => {

				if( !result || !result.verarbeitet ) return of(false);

				return this.httpClient.get<boolean>(
					this.environment.backendUrl + 'veranstaltungKategorie/status/' + eventId
				).pipe(
					map( answer => {

						if(  ( answer === null ) || ( answer === undefined )  ) {
							return false;
						}

						if( answer ) {
							this.matSnackBar.open('Veranstaltung abgemeldet', '');
							if( !this.subscribedEventIds.includes(eventId) ) {
								this.subscribedEventIds.push(eventId);
							}
							this.unsubscribedEventIds = this.unsubscribedEventIds.filter(id => id !== eventId);
						}
						else {
							this.matSnackBar.open('Veranstaltung angemeldet', '');
							this.removeEventFromSubscribedEvents(eventId);
						}

						return true;
					})
				);

			}),
			catchError( err => {
				this.matDialog.open( InfoDialogComponent, {
					data: { title: 'Fehler', message: 'Beim Überprüfen des Status der Veranstaltung ist ein Fehler aufgetreten' }
				});
				return of(false);
			})
		);
	}


  getCalendarGroupsAndCategories(): Observable<{ groups: CalendarGroup[], categories: EventCategory[] }> {

    return forkJoin({
      groups: this.getCalendarGroups(),
      categories: this.getCategories()
    });

  }


	getCategories(): Observable<EventCategory[]> {

		return this.httpClient.get<EventCategory[]>(
			this.environment.backendUrl + 'fragenkategorie/all',
		).pipe(
			map( categories => categories || [] ),
			catchError( err => {
				this.matDialog.open( InfoDialogComponent, {
					data: {title: 'Fehler', message: 'Beim Abrufen von Formularen ist ein Fehler aufgetreten'}
				});
				return of([]);
			})
		);

	}


  downloadFormAnswers(veranstaltungId: string, csvFileName: string) {
    this.httpClient.get(
      this.environment.backendUrl + 'veranstaltungKategorie/getCSV/' + veranstaltungId,
      {responseType: 'blob'}
    ).subscribe((data: Blob) => {
      // prepend UTF-8 BOM so Excel interprets special characters correctly
      const utf8Bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([utf8Bom, data], {type: 'text/csv;charset=utf-8'});

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      // Set the Content-Disposition header to specify the file name
      //   const headers = new HttpHeaders().append('Content-Disposition', `attachment; filename="${fileName}"`);

      console.warn(data);
      // Use the file name from the headers to set the download attribute
      link.download = csvFileName;

      // Append the link to the DOM and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up the link element and the URL object
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    });
  }

  getUnsubscribedEventIds(): string[] {
    return structuredClone(this.unsubscribedEventIds);
  }

  downloadICSFile(veranstaltungId: string) {
    const url = this.environment.backendUrl + 'event/ics/' + veranstaltungId;
    this.httpClient.get(
      url,
      {responseType: 'blob', observe: 'response'} // Add the observe option
    ).subscribe((response: any) => { // Use 'response' instead of 'data'
      const blob = new Blob([response.body], {type: 'text/csv'});

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      const filename = 'event.ics';

      // Set the filename for download
      link.download = decodeURIComponent(filename);

      // Append the link to the DOM and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up the link element and the URL object
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      this.matSnackBar.open('ICS-Datei heruntergeladen', 'Erfolg');
    }, error => {
      this.matDialog.open(InfoDialogComponent, {
        data: {title: 'Fehler', message: 'Beim Herunterladen der ICS-Datei ist ein Fehler aufgetreten'}
      });
    });
  }


}
